/**
 * ConnectDatabaseDialog — shared "connect to a database" modal for every
 * agent that needs one (Data Profiler, Data Guardian/Masking, Data
 * Converter, Semantic Intelligence, Metadata Curator, MDM).
 *
 * Owns the UI + the project's saved-connection list (fetched fresh each
 * time it opens) so a user can reuse a connection any agent already made
 * for this project instead of re-entering credentials — and so a future
 * change to that picker UX (or the field set, or the list-fetch logic)
 * happens in exactly one place instead of five+ copy-pasted forms.
 *
 * Deliberately does NOT know how any specific agent actually establishes a
 * connection (each agent's backend has its own downstream side effects —
 * the profiler starts a profiling session, masking loads maskable tables,
 * converter starts a migration session, ...). The caller supplies that via
 * two callbacks:
 *   - onConnectExisting(connectionId, extraFieldValue): reuse a saved connection.
 *   - onConnectNew(values): a fresh manual connection (values.extraField holds it too).
 * Both return {ok, error?} so the dialog can show an inline error and stay
 * open on failure, or close itself on success.
 *
 * `testIdPrefix` (default "db-connect") — every consumer uses the SAME
 * test-id scheme so Playwright specs don't each invent their own; see
 * frontend/e2e/specs/{data-masking-flow,data-model-creator,metadata-catalog,mdm}.spec.ts.
 *
 * `extraField` — a single optional per-agent field this generic dialog
 * doesn't otherwise know about (masking/semantic's "which table to load",
 * MDM's "table_or_query"). Rendered above the picker/form split since it's
 * needed regardless of whether the user reuses a saved connection or enters
 * a new one.
 */

import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, Database, X } from "lucide-react";
import { apiv2, type ConnectorOut, type DBConfigOut } from "../../apiv2";
import { DialogOverlay } from "../ui/DialogOverlay";
import { FormField } from "../ui/FormField";
import {
  savedConnectionDetail,
  savedConnectionEndpoint,
  savedConnectionTitle,
} from "../../lib/savedConnectionLabel";

export interface DbConnectFormValues {
  db_type: string;
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  schema_name: string;
  // User-chosen label for this connection when it gets saved to the
  // project's shared connection library. "" (the default) keeps the
  // library's auto-generated "{kind} DB ({db_type})" name.
  connection_name: string;
  // Snowflake
  account?: string;
  warehouse?: string;
  role?: string;
  // Databricks
  service_name?: string;
  http_path?: string;
  client_id?: string;
  client_secret?: string;
  // BigQuery
  project?: string;
  bigquery_credentials_json?: string;
  // jdbc_bridge (mainframe DB2 / ADABAS SQL Gateway / any JDBC source)
  jdbc_driver_class?: string;
  jdbc_url?: string;
  jdbc_driver_jar_path?: string;
  bridge_label?: string;
  // Index signature so the generic extra-field renderer below (driven by
  // the connector registry's per-db_type extra_fields list) can read/write
  // any of the named fields above by their string key, or a future
  // registry-added field not yet named here explicitly.
  [key: string]: string | undefined;
}

export interface ConnectResult {
  ok: boolean;
  error?: string;
}

export interface DbTypeOption {
  value: string;
  label: string;
}

export interface ExtraFieldConfig {
  key: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  testId?: string;
}

// Fallback only — used for the brief window before the registry fetch
// resolves, or if a caller passes an explicit dbTypeOptions override
// instead of agentKey. Every real caller should pass agentKey so the
// options come from ztd_sdk.connectors instead of drifting out of sync
// with it (the root cause of two real bugs: Data Converter's dropdown
// missing Snowflake, the Profiler's dropdown advertising MariaDB/Redshift
// with zero backend support).
const DEFAULT_DB_TYPE_OPTIONS: DbTypeOption[] = [
  { value: "postgresql", label: "PostgreSQL" },
  { value: "mysql", label: "MySQL" },
  { value: "mssql", label: "SQL Server" },
  { value: "oracle", label: "Oracle" },
  { value: "snowflake", label: "Snowflake" },
  { value: "databricks", label: "Databricks" },
];

const DEFAULT_FORM_VALUES: DbConnectFormValues = {
  db_type: "postgresql",
  host: "",
  port: "5432",
  database: "",
  username: "",
  password: "",
  schema_name: "public",
  connection_name: "",
  account: "",
  warehouse: "",
  role: "",
  service_name: "",
  http_path: "",
  client_id: "",
  client_secret: "",
};

export interface ConnectDatabaseDialogProps {
  open: boolean;
  onClose: () => void;
  /** Which agent this is for, shown in the dialog title, e.g. "Connect Database". */
  title?: string;
  projectId: string | undefined;
  /** "source" (default) or "target" — which of the project's saved-connection lists to offer. */
  kind?: "source" | "target";
  /** Which agent's connector registry entries to fetch, e.g.
   * "data_converter", "profiler", "mdm" — drives both the Database Type
   * options AND each option's extra-field set (Snowflake's
   * account/warehouse/role, Databricks' http_path/client_id/..., BigQuery's
   * project/credentials, jdbc_bridge's driver fields, ...). Omit for the
   * union across every agent (e.g. a cross-agent connections-management
   * view not scoped to one agent). */
  agentKey?: string;
  /** Include file_path/file-kind entries (sqlite, duckdb, mainframe_copybook)
   * in the fetched options — only for a caller with a real file-based
   * connect flow; the default (network + bridge only) is correct for a
   * normal live-connection dialog. */
  includeFileSources?: boolean;
  /** Manual override — when given, skips the registry fetch entirely and
   * uses this list as-is (no per-option extra fields). Only for a caller
   * that genuinely can't use the registry; every normal caller should omit
   * this and pass agentKey instead. */
  dbTypeOptions?: DbTypeOption[];
  defaultDbType?: string;
  defaultSchemaName?: string;
  /** One extra per-agent field (e.g. "table" / "table_or_query"), if this agent needs one. */
  extraField?: ExtraFieldConfig;
  /** Shared test-id scheme every consumer uses — default matches the
   * originally-MDM-authored, already agent-agnostic convention. */
  testIdPrefix?: string;
  /** Skip the saved-connection picker entirely and open straight on the
   * credential form — for callers whose OWN trigger already means "add a
   * new one" (e.g. a connections-management view that lists connections
   * itself and has its own "+ Add connection" button), where showing the
   * picker again would just be a confusing echo of what's already on
   * screen. Also skips the list fetch, since it'd be unused. */
  startOnNewForm?: boolean;
  // `label` — the saved row's display name/db_type·host, so the caller can
  // show something better than a generic "saved connection" string once
  // connected (the connect response itself doesn't carry host/name back).
  onConnectExisting: (
    connectionId: string,
    label?: string,
  ) => Promise<ConnectResult>;
  onConnectNew: (values: DbConnectFormValues) => Promise<ConnectResult>;
}

/**
 * Strip surrounding whitespace from the pasted identifier fields.
 *
 * A trailing space on a pasted hostname produces a DNS/connection failure
 * that reads as "the connector is broken" rather than "there is an invisible
 * character in this field" — it has cost real debugging time. The password is
 * deliberately NOT trimmed: leading/trailing whitespace can be significant in
 * a credential, and silently altering one would be worse than a failed login.
 */
function trimConnectionFields(
  values: DbConnectFormValues,
): DbConnectFormValues {
  const trimmed = { ...values };
  for (const key of [
    "host",
    "port",
    "database",
    "username",
    "schema_name",
    "connection_name",
  ] as const) {
    const v = trimmed[key];
    if (typeof v === "string") {
      trimmed[key] = v.trim();
    }
  }
  return trimmed;
}

export function ConnectDatabaseDialog({
  open,
  onClose,
  title = "Connect Database",
  projectId,
  kind = "source",
  agentKey,
  includeFileSources = false,
  dbTypeOptions: dbTypeOptionsOverride,
  defaultDbType,
  defaultSchemaName,
  extraField,
  testIdPrefix = "db-connect",
  startOnNewForm = false,
  onConnectExisting,
  onConnectNew,
}: ConnectDatabaseDialogProps) {
  const [connectors, setConnectors] = useState<ConnectorOut[]>([]);
  const [savedConnections, setSavedConnections] = useState<DBConfigOut[]>([]);
  const [savedConnectionsLoading, setSavedConnectionsLoading] = useState(false);
  const [showNewConnectionForm, setShowNewConnectionForm] =
    useState(startOnNewForm);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Set when a saved-connection attempt fails, so the picker offers an
  // inline "re-enter the password and retry" prompt right there instead of
  // just showing red text — covers both "stored password could not be
  // found" (secret lost, e.g. an ephemeral dev Vault reset) and "the
  // password itself is wrong/expired", since both are fixed the same way.
  const [passwordFixId, setPasswordFixId] = useState<string | null>(null);
  const [fixPassword, setFixPassword] = useState("");
  const [fixingPassword, setFixingPassword] = useState(false);
  const [form, setForm] = useState<DbConnectFormValues>({
    ...DEFAULT_FORM_VALUES,
    db_type: defaultDbType ?? DEFAULT_FORM_VALUES.db_type,
    schema_name: defaultSchemaName ?? DEFAULT_FORM_VALUES.schema_name,
  });

  useEffect(() => {
    if (!open || !projectId || startOnNewForm) return;
    let cancelled = false;
    setError(null);
    setPasswordFixId(null);
    setFixPassword("");
    setSavedConnectionsLoading(true);
    const list =
      kind === "target"
        ? apiv2.listTargetConnections(projectId)
        : apiv2.listSourceConnections(projectId);
    list
      .then((conns) => {
        if (cancelled) return;
        setSavedConnections(conns);
        // No saved connections yet — skip the picker, go straight to the form.
        setShowNewConnectionForm(conns.length === 0);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedConnections([]);
        setShowNewConnectionForm(true);
      })
      .finally(() => {
        if (!cancelled) setSavedConnectionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId, kind, startOnNewForm]);

  // Fetches the Database Type options (+ each option's extra-field set)
  // from the shared connector registry, scoped to agentKey (or the union
  // across every agent if omitted). Skipped when the caller passes an
  // explicit dbTypeOptions override instead.
  useEffect(() => {
    if (!open || dbTypeOptionsOverride) return;
    let cancelled = false;
    apiv2
      .listSupportedConnectors(agentKey, includeFileSources)
      .then((result) => {
        if (!cancelled) setConnectors(result);
      })
      .catch(() => {
        if (!cancelled) setConnectors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, agentKey, includeFileSources, dbTypeOptionsOverride]);

  const dbTypeOptions: DbTypeOption[] =
    dbTypeOptionsOverride ??
    (connectors.length > 0
      ? connectors.map((c) => ({ value: c.db_type, label: c.label }))
      : DEFAULT_DB_TYPE_OPTIONS);

  const activeConnector = connectors.find((c) => c.db_type === form.db_type);

  const handleConnectSaved = useCallback(
    async (connectionId: string, label?: string) => {
      if (extraField?.required && !extraField.value) {
        setError(`${extraField.label} is required.`);
        return;
      }
      setError(null);
      setSubmitting(true);
      try {
        const result = await onConnectExisting(connectionId, label);
        if (result.ok) {
          setPasswordFixId(null);
          setFixPassword("");
          onClose();
        } else {
          setError(
            result.error ?? "Couldn't connect using that saved connection.",
          );
          // Any failed saved-connection attempt (a missing stored secret,
          // an expired/rotated password, ...) is recoverable the same
          // way — offer the inline "re-enter the password" prompt rather
          // than forcing delete-and-re-add-from-scratch.
          setPasswordFixId(connectionId);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onConnectExisting, onClose, extraField],
  );

  // Updates the saved connection's stored password (client_secret too, if
  // this dialog's db types ever need one) via the shared project_db_config
  // PATCH endpoint, then immediately retries connecting with it.
  const handleFixPasswordAndRetry = useCallback(async () => {
    if (!passwordFixId || !projectId) return;
    const password = fixPassword.trim();
    if (!password) {
      setError("Enter the password to reconnect.");
      return;
    }
    setFixingPassword(true);
    setError(null);
    try {
      const updateSecret =
        kind === "target"
          ? apiv2.updateTargetConnectionSecret
          : apiv2.updateSourceConnectionSecret;
      await updateSecret(projectId, passwordFixId, { password });
      setFixPassword("");
      await handleConnectSaved(passwordFixId);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to update the stored password.",
      );
    } finally {
      setFixingPassword(false);
    }
  }, [passwordFixId, fixPassword, projectId, kind, handleConnectSaved]);

  const handleSubmitNew = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        const result = await onConnectNew(trimConnectionFields(form));
        if (result.ok) {
          onClose();
        } else {
          setError(result.error ?? "Connection failed.");
        }
      } finally {
        setSubmitting(false);
      }
    },
    [onConnectNew, form, onClose],
  );

  if (!open) return null;

  return (
    <DialogOverlay onClose={onClose}>
      <div
        className="glass-surface rounded-2xl p-8 max-w-lg w-full mx-4 space-y-5"
        data-testid={`${testIdPrefix}-modal`}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-heading-2 text-[var(--foreground)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-white/5"
          >
            <X className="w-5 h-5 text-[var(--muted-foreground)]" />
          </button>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        {passwordFixId && !showNewConnectionForm && (
          <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 space-y-2">
            <p className="text-xs text-[var(--muted-foreground)]">
              Re-enter the password for{" "}
              <span className="text-[var(--foreground)] font-medium">
                {(() => {
                  const fixing = savedConnections.find(
                    (c) => c.id === passwordFixId,
                  );
                  return fixing
                    ? savedConnectionTitle(fixing)
                    : "this connection";
                })()}
              </span>{" "}
              to reconnect.
            </p>
            <input
              type="password"
              autoFocus
              data-testid={`${testIdPrefix}-fix-password`}
              value={fixPassword}
              onChange={(e) => setFixPassword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleFixPasswordAndRetry();
              }}
              placeholder="Password"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                data-testid={`${testIdPrefix}-fix-password-submit`}
                onClick={handleFixPasswordAndRetry}
                disabled={fixingPassword}
                className="flex-1 py-2 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {fixingPassword ? "Reconnecting…" : "Reconnect"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPasswordFixId(null);
                  setFixPassword("");
                  setError(null);
                }}
                disabled={fixingPassword}
                className="px-3 py-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] text-sm hover:text-[var(--foreground)] transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {extraField && (
          <FormField label={extraField.label}>
            <input
              type="text"
              data-testid={
                extraField.testId ?? `${testIdPrefix}-table-or-query`
              }
              value={extraField.value}
              onChange={(e) => extraField.onChange(e.target.value)}
              placeholder={extraField.placeholder}
              required={extraField.required}
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
            />
          </FormField>
        )}

        {/* ── Saved-connection picker ── */}
        {!showNewConnectionForm && (
          <div className="space-y-3">
            {savedConnectionsLoading ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                Loading saved connections…
              </p>
            ) : (
              <>
                <p className="text-xs text-[var(--muted-foreground)]">
                  Saved connections for this project — reuse one instead of
                  re-entering credentials.
                </p>
                {savedConnections.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    disabled={submitting}
                    data-testid={`${testIdPrefix}-saved-connection`}
                    data-connection-id={c.id}
                    onClick={() =>
                      handleConnectSaved(c.id, savedConnectionTitle(c))
                    }
                    className="w-full text-left px-4 py-3 rounded-lg bg-white/5 border border-[var(--border)] hover:border-[var(--primary)]/50 transition-colors disabled:opacity-50"
                  >
                    <div className="text-sm font-medium text-[var(--foreground)] flex items-center gap-2">
                      <Database className="w-3.5 h-3.5 text-[var(--muted-foreground)]" />
                      {savedConnectionTitle(c)}
                    </div>
                    <div className="text-xs text-[var(--muted-foreground)] mt-0.5 break-all">
                      {savedConnectionEndpoint(c)}
                    </div>
                    {savedConnectionDetail(c) && (
                      <div className="text-xs text-[var(--muted-foreground)]/80 mt-0.5">
                        {savedConnectionDetail(c)}
                      </div>
                    )}
                  </button>
                ))}
              </>
            )}
            <button
              type="button"
              data-testid={`${testIdPrefix}-add-new`}
              onClick={() => setShowNewConnectionForm(true)}
              className="w-full py-2.5 rounded-lg border border-dashed border-[var(--border)] text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--primary)]/50 transition-colors"
            >
              + Add new connection
            </button>
          </div>
        )}

        {/* ── Fresh-credentials form ── */}
        {showNewConnectionForm && (
          <form
            onSubmit={handleSubmitNew}
            className="space-y-5"
            data-testid={`${testIdPrefix}-form`}
          >
            {savedConnections.length > 0 && (
              <button
                type="button"
                data-testid={`${testIdPrefix}-back`}
                onClick={() => setShowNewConnectionForm(false)}
                className="text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] flex items-center gap-1"
              >
                <ChevronLeft className="w-3 h-3" />
                Use an existing connection
              </button>
            )}

            <FormField label="Connection Name (optional)">
              <input
                type="text"
                data-testid={`${testIdPrefix}-connection-name`}
                value={form.connection_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, connection_name: e.target.value }))
                }
                placeholder={`${kind === "target" ? "Target" : "Source"} DB (${form.db_type})`}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
              />
            </FormField>

            <FormField label="Database Type">
              <select
                data-testid={`${testIdPrefix}-type`}
                value={form.db_type}
                onChange={(e) => {
                  const nextDbType = e.target.value;
                  const prevDefaultPort = activeConnector?.default_port
                    ? String(activeConnector.default_port)
                    : undefined;
                  const nextConnector = connectors.find(
                    (c) => c.db_type === nextDbType,
                  );
                  const nextDefaultPort = nextConnector?.default_port;
                  const prevDefaultSchema = activeConnector?.default_schema;
                  const nextDefaultSchema = nextConnector?.default_schema;
                  setForm((f) => {
                    // Only auto-fill the port if it's still at the previous
                    // engine's default (or empty) — don't clobber a port the
                    // user has manually edited (e.g. a tunnel like 5433).
                    const isUntouched = !f.port || f.port === prevDefaultPort;
                    // Same rule for the schema. Without this the form kept
                    // the Postgres-shaped default ("public") after switching
                    // to SQL Server, and table discovery binds that value
                    // straight into `WHERE table_schema = :s` — so it found
                    // zero tables and surfaced as "Connection failed" with
                    // nothing wrong with the connection.
                    const schemaUntouched =
                      !f.schema_name ||
                      f.schema_name === prevDefaultSchema ||
                      f.schema_name === DEFAULT_FORM_VALUES.schema_name;
                    return {
                      ...f,
                      db_type: nextDbType,
                      port:
                        isUntouched && nextDefaultPort
                          ? String(nextDefaultPort)
                          : f.port,
                      schema_name: schemaUntouched
                        ? (nextDefaultSchema ?? "")
                        : f.schema_name,
                    };
                  });
                }}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm"
              >
                {dbTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <FormField label="Host">
                  <input
                    type="text"
                    data-testid={`${testIdPrefix}-host`}
                    value={form.host}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, host: e.target.value }))
                    }
                    placeholder="localhost"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
                    required
                  />
                </FormField>
              </div>
              <FormField label="Port">
                <input
                  type="text"
                  data-testid={`${testIdPrefix}-port`}
                  value={form.port}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, port: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm"
                />
              </FormField>
            </div>

            <FormField label="Database">
              <input
                type="text"
                data-testid={`${testIdPrefix}-database`}
                value={form.database}
                onChange={(e) =>
                  setForm((f) => ({ ...f, database: e.target.value }))
                }
                placeholder="my_database"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
                required
              />
            </FormField>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Username">
                <input
                  type="text"
                  data-testid={`${testIdPrefix}-username`}
                  value={form.username}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, username: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
                  required
                />
              </FormField>
              <FormField label="Password">
                <input
                  type="password"
                  data-testid={`${testIdPrefix}-password`}
                  value={form.password}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, password: e.target.value }))
                  }
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm"
                />
              </FormField>
            </div>

            <FormField label="Schema">
              <input
                type="text"
                data-testid={`${testIdPrefix}-schema`}
                value={form.schema_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, schema_name: e.target.value }))
                }
                placeholder="public"
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm placeholder:text-[var(--muted-foreground)]"
              />
            </FormField>

            {activeConnector && activeConnector.extra_fields.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {activeConnector.extra_fields.map((field) => (
                  <FormField key={field.key} label={field.label}>
                    <input
                      type={field.secret ? "password" : "text"}
                      data-testid={`${testIdPrefix}-${field.key}`}
                      value={form[field.key] ?? ""}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [field.key]: e.target.value }))
                      }
                      placeholder={field.default ?? undefined}
                      required={field.required}
                      title={field.help_text ?? undefined}
                      className="w-full px-3 py-2 rounded-lg bg-white/5 border border-[var(--border)] text-[var(--foreground)] text-sm"
                    />
                  </FormField>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid={`${testIdPrefix}-submit`}
              className="w-full py-2.5 rounded-lg bg-[var(--primary)] text-[var(--primary-foreground)] font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              <Database className="w-4 h-4 inline mr-2" />
              {submitting ? "Connecting…" : "Connect"}
            </button>
          </form>
        )}
      </div>
    </DialogOverlay>
  );
}
